<script setup lang="ts">
import type { Organization } from '@/stores/admin'
import { useAdmin } from '@/stores/admin'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { FormSubmitEvent } from '@nuxt/ui'
import AdminOrganizationForm from '@/views/forms/AdminOrganizationForm.vue'
import type { OrganizationFormSchema } from '@/views/forms/schema.ts'
import { getStytchOrganizationUrl } from '@/lib/stytch'
import { handleError } from '@/lib/error'

const route = useRoute()
const router = useRouter()
const admin = useAdmin()

if (admin.organizations.length === 0) {
  await admin.fetchOrganizations()
}

const organization = ref<Organization | undefined>(
  admin.organizations.find((org: Organization) => org.slug === (route.params.slug as string)),
)

const isTogglingActive = ref(false)

async function onSubmit(
  event: FormSubmitEvent<OrganizationFormSchema & { delete_logo?: boolean }>,
) {
  let orgId!: string
  try {
    if (!organization.value) {
      const newOrg = await admin.createOrganization(event.data)
      orgId = newOrg.id
    } else {
      await admin.updateOrganization(organization.value.id, event.data)
      orgId = organization.value.id
    }
    if (event.data.logo_file) {
      await admin.saveOrganizationLogo(orgId, event.data.logo_file)
    } else if (event.data.delete_logo) {
      await admin.deleteOrganizationLogo(orgId)
    }
    router.replace({ name: 'admin-organization', params: { slug: event.data.slug } })
  } catch (error) {
    handleError(error, 'Failed to save organization')
  } finally {
    organization.value = admin.organizations.find(
      (org: Organization) => org.slug === event.data.slug,
    )
  }
}

async function toggleActive(isActive: boolean) {
  if (!organization.value || isTogglingActive.value) return
  isTogglingActive.value = true
  try {
    await admin.toggleOrganizationActive(organization.value.id, isActive)
    organization.value = admin.organizations.find(
      (org: Organization) => org.id === organization.value?.id,
    )
  } catch (error) {
    handleError(error, 'Failed to update organization status')
  } finally {
    isTogglingActive.value = false
  }
}

async function deleteOrganization() {
  if (!organization.value) return
  if (!confirm(`Delete "${organization.value.name}"? This cannot be undone.`)) return
  try {
    await admin.deleteOrganization(organization.value.id)
    router.replace({ name: 'admin' })
  } catch (error) {
    handleError(error, 'Failed to delete organization')
  }
}
</script>

<template>
  <AppComponent
    id="admin-organization"
    :title="organization ? `Organization: ${organization?.name}` : 'New organization'"
  >
    <div class="flex flex-col gap-2 sm:gap-4 lg:gap-4 w-full lg:max-w-2xl mx-auto">
      <UPageCard
        title="Organization details"
        :description="
          organization ? 'Update the organization details below.' : 'Create a new organization.'
        "
        variant="naked"
        orientation="horizontal"
      >
        <UButton
          :to="getStytchOrganizationUrl(organization.stytch_organization_id)"
          target="_blank"
          icon="i-lucide-external-link"
          variant="outline"
          color="neutral"
          class="w-fit lg:ms-auto"
          v-if="organization?.stytch_organization_id"
        >
          Open in Stytch
        </UButton>
        <p v-else-if="organization">
          This organization is not linked to a Stytch organization. Clicking on "Update
          organization" will create a new Stytch organization and link the two together.
        </p>
      </UPageCard>
      <UPageCard variant="subtle">
        <AdminOrganizationForm
          :organization="organization"
          :onSubmit="onSubmit"
          :submitButtonText="organization ? 'Update organization' : 'Create organization'"
        />
      </UPageCard>

      <template v-if="organization">
        <!-- Status -->
        <UPageCard
          title="Status"
          description="Control whether this organization is active."
          variant="naked"
          orientation="horizontal"
        >
          <div class="flex items-center gap-3 lg:ms-auto">
            <span class="text-sm text-muted">{{
              organization.is_active ? 'Active' : 'Inactive'
            }}</span>
            <USwitch
              :model-value="organization.is_active"
              :disabled="isTogglingActive"
              @update:model-value="toggleActive"
            />
          </div>
        </UPageCard>

        <!-- Impersonation -->
        <UPageCard
          title="Impersonation"
          description="Preview the app as this organization would see it."
          variant="naked"
          orientation="horizontal"
        >
          <UButton
            :to="`/admin/preview/${organization.id}`"
            target="_blank"
            icon="i-lucide-eye"
            variant="solid"
            color="warning"
            class="w-fit lg:ms-auto"
          >
            View as {{ organization.name }}
          </UButton>
        </UPageCard>

        <!-- Danger Zone -->
        <UPageCard
          title="Danger zone"
          description="Permanently delete this organization and all its data."
          variant="naked"
          orientation="horizontal"
        >
          <UButton
            icon="i-lucide-trash-2"
            variant="solid"
            color="error"
            class="w-fit lg:ms-auto"
            @click="deleteOrganization"
          >
            Delete organization
          </UButton>
        </UPageCard>
      </template>
    </div>
  </AppComponent>
</template>
